import Icon, { ExclamationOutlined, WarningTwoTone } from '@ant-design/icons';
import { StepProps, Tooltip } from 'antd';
import { prettifyErrors } from 'common/helpers';
import { BtlApplicationFormPageEnum, SectionProblems } from './types';
import { TypedFormatMessageFn } from 'types/intl';
import { HouseholdsToHasIndividuals } from 'api/btlApplicationForm/types';
import { STATUS_TO_STEPS } from './constants';

export const getProblemsSubtitle = (
  section: BtlApplicationFormPageEnum,
  problems: SectionProblems | undefined,
  formatMessage: TypedFormatMessageFn,
  subjects?: string[],
  householdsToHasIndividuals?: HouseholdsToHasIndividuals,
  isAffordabilityRunRequired?: boolean,
): StepProps['subTitle'] => {
  const fieldsToSkip = section === BtlApplicationFormPageEnum.PRODUCT ? ['isCurrentProductSuitable'] : [];

  const missingFieldsToRender =
    section === BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL
      ? Array.isArray(problems?.[section])
        ? problems?.[section].flatMap(item => prettifyErrors(item, fieldsToSkip))
        : []
      : typeof problems?.[section] === 'object' && !Array.isArray(problems?.[section])
      ? prettifyErrors(
          Object.fromEntries(
            Object.entries(problems?.[section])
              .filter(([, value]) => Array.isArray(value))
              .map(([key, value]) => [key, value as string[]]),
          ),
          fieldsToSkip,
        )
      : [];

  const renderTooltip = (title: React.ReactNode, key: number) => (
    <Tooltip title={title} key={key} placement='right' className='mr-2'>
      <WarningTwoTone style={{ fontSize: 20 }} twoToneColor={'#ffcc00'} />
    </Tooltip>
  );

  const renderErrorList = (errors: string[], header: string) => (
    <ul className='px-0 mr-2 my-0'>
      {header}
      {errors.map(error => (
        <li className='ml-8' key={error}>
          {error}
        </li>
      ))}
    </ul>
  );

  const missingFieldsTooltip = missingFieldsToRender.map((errorArr, index) =>
    errorArr.length > 0
      ? renderTooltip(
          subjects ? (
            <>
              <h4 className='my-1'>{`${subjects[index]}`}</h4>
              {renderErrorList(errorArr, formatMessage({ id: 'btlApplicationForm.missingFields.title' }))}
            </>
          ) : (
            renderErrorList(errorArr, formatMessage({ id: 'btlApplicationForm.missingFields.title' }))
          ),
          index,
        )
      : null,
  );

  const productSuitabilityTooltip =
    section === BtlApplicationFormPageEnum.PRODUCT &&
    !Array.isArray(problems?.[section]) &&
    problems &&
    !problems?.[section]?.isCurrentProductSuitable
      ? renderTooltip(renderErrorList([formatMessage({ id: 'btlApplicationForm.PRODUCT.notSuitable.title' })], ''), 0)
      : null;

  const householdsThatNeedIndividuals =
    section === BtlApplicationFormPageEnum.TOP_SLICING && householdsToHasIndividuals
      ? Object.entries(householdsToHasIndividuals)
          .filter(([, value]) => value === false)
          .map(([key]) => key)
      : [];

  const householdsNeedIndividualTooltip =
    section === BtlApplicationFormPageEnum.TOP_SLICING &&
    householdsToHasIndividuals &&
    householdsThatNeedIndividuals.length > 0
      ? renderTooltip(
          renderErrorList([], formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.noIndividuals.tooltip' })),
          0,
        )
      : undefined;

  const affordabilityRunRequiredTooltip =
    section === BtlApplicationFormPageEnum.AFFORDABILITY_CHECK && isAffordabilityRunRequired
      ? renderTooltip('A new affordability run is required before you can submit.', 9999)
      : null;

  return (
    <>
      {missingFieldsTooltip}
      {productSuitabilityTooltip}
      {householdsNeedIndividualTooltip}
      {affordabilityRunRequiredTooltip}
    </>
  );
};

export const getIcon = (
  activeIndex: number | undefined,
  componentIndex: number,
  section: BtlApplicationFormPageEnum,
  problems: SectionProblems | undefined,
  currentProduct?: any,
  areMandatoryFieldsComplete?: boolean,
  householdsToHasIndividuals?: HouseholdsToHasIndividuals,
  isAffordabilityRunRequired?: boolean,
): StepProps['icon'] => {
  // Show warning icon for outdated affordability run first
  if (section === BtlApplicationFormPageEnum.AFFORDABILITY_CHECK && isAffordabilityRunRequired) {
    return (
      <Tooltip
        title='A new Affordability Check is required before you can submit this application.'
        placement='right'
        key={activeIndex}
      >
        <ExclamationOutlined
          style={{ transition: 'unset' }}
          className={'bg-yellow-500 rounded-[32px] p-1 text-white'}
        />
      </Tooltip>
    );
  }

  let okIcon = (
    <Icon
      className='ant-steps-item-icon py-2'
      component={() => (
        <div className={`rounded-[32px] ${activeIndex === componentIndex ? 'text-white' : ''}`}>
          {componentIndex + 1}
        </div>
      )}
    />
  );
  if (areMandatoryFieldsComplete === false) {
    okIcon = (
      <Tooltip title='Please complete the preceding steps before continuing' placement='right' key={activeIndex}>
        {okIcon}
      </Tooltip>
    );
  }
  if (section === BtlApplicationFormPageEnum.TOP_SLICING && householdsToHasIndividuals) {
    const householdsThatNeedIndividuals = Object.entries(householdsToHasIndividuals)
      .filter(([, value]) => value === false)
      .map(([key]) => key);
    if (householdsThatNeedIndividuals.length > 0) {
      return (
        <ExclamationOutlined
          style={{ transition: 'unset' }}
          className={'bg-yellow-500 rounded-[32px] p-1 text-white'}
        />
      );
    }
  }
  const warnIcon = (
    <ExclamationOutlined style={{ transition: 'unset' }} className={'bg-yellow-500 rounded-[32px] p-1 text-white'} />
  );
  if (!problems) {
    return okIcon;
  }
  const problemsData =
    section === BtlApplicationFormPageEnum.APPLICANTS
      ? [
          ...(Array.isArray(problems?.[BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL])
            ? problems?.[BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL] ?? []
            : []),
          ...(Array.isArray(problems?.[BtlApplicationFormPageEnum.APPLICANTS_COMPANY])
            ? problems?.[BtlApplicationFormPageEnum.APPLICANTS_COMPANY] ?? []
            : []),
        ]
      : problems[section];
  if (!problemsData) {
    return okIcon;
  }
  if (!Array.isArray(problemsData) && !problemsData.missingFields && !!problemsData.isCurrentProductSuitable) {
    return okIcon;
  }
  if (section === 'APPLICANTS') {
    let hasProblem = false;
    if (Array.isArray(problemsData)) {
      problemsData.forEach((field: any) => {
        if (field.missingFields.length > 0 && activeIndex !== componentIndex) {
          hasProblem = true;
        }
      });
    }
    if (!Array.isArray(problemsData) && problemsData?.isCurrentProductSuitable !== undefined) {
      hasProblem = !problemsData.isCurrentProductSuitable;
    }
    return hasProblem ? warnIcon : okIcon;
  } else {
    if (!Array.isArray(problemsData) && problemsData?.missingFields?.length > 0 && activeIndex !== componentIndex) {
      return warnIcon;
    }
  }
  if (
    currentProduct &&
    section === BtlApplicationFormPageEnum.PRODUCT &&
    activeIndex !== componentIndex &&
    !Array.isArray(problemsData) &&
    !problemsData?.isCurrentProductSuitable
  ) {
    return warnIcon;
  }

  return okIcon;
};

export const stepItems = (
  formatMessage: TypedFormatMessageFn,
  data?: any,
  activeIndex?: number,
  updateSectionInUrl?: (section: BtlApplicationFormPageEnum) => void,
  isAffordabilityPassed?: boolean,
  problems?: SectionProblems,
  areMandatoryFieldsComplete?: boolean,
  householdsToHasIndividuals?: HouseholdsToHasIndividuals,
  isAffordabilityRunRequired?: boolean,
): StepProps[] => {
  const showStepAtStatus = (status: string | undefined, page: BtlApplicationFormPageEnum) => {
    if (!status) return false;
    const steps = STATUS_TO_STEPS[status as keyof typeof STATUS_TO_STEPS];
    return steps?.includes(page);
  };

  return [
    ...(showStepAtStatus(data?.status, BtlApplicationFormPageEnum.AFFORDABILITY_CHECK) || data === null
      ? [
          {
            title: formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.label' }),
            disabled: !data?.product,
            className: data?.product ? 'cursor-pointer' : undefined,
            subTitle: getProblemsSubtitle(
              BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
              !data?.product ? undefined : problems,
              formatMessage,
              undefined,
              undefined,
              isAffordabilityRunRequired,
            ),
            icon: getIcon(
              activeIndex,
              data?.isTopSlicing ? 4 : 3,
              BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
              !data?.product ? undefined : problems,
              undefined,
              undefined,
              undefined,
              isAffordabilityRunRequired,
            ),
            style: {
              transition: 'unset',
            },
            onClick: () => data?.product && updateSectionInUrl?.(BtlApplicationFormPageEnum.AFFORDABILITY_CHECK),
          },
        ]
      : []),
  ];
};
